import { createServerFn } from "@tanstack/react-start";

export type CatalogMapAudit = {
  db: {
    sites_total: number;
    sites_published: number;
    sites_published_with_gps: number;
    venues_total: number;
    venues_published: number;
    venues_published_with_gps: number;
  };
  expected_pins: number; // sites+venues published with GPS = what map SHOULD show
};

export type CatalogMapAuditRow = {
  id: string;
  layer: "venue" | "brewery_site";
  name: string;
  city: string | null;
  country_iso2: string | null;
};

export const getCatalogMapAudit = createServerFn({ method: "GET" }).handler(
  async (): Promise<CatalogMapAudit> => {
    const { createClient } = await import("@supabase/supabase-js");
    const supabase = createClient(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_PUBLISHABLE_KEY!,
      { auth: { persistSession: false, autoRefreshToken: false } },
    );

    async function count(
      table: "cid_brewery_sites" | "cid_venues",
      filter: "total" | "published" | "published_gps",
    ): Promise<number> {
      let q = supabase.from(table).select("id", { count: "exact", head: true });
      if (filter === "published" || filter === "published_gps") {
        q = q.eq("publish_status", "PUBLISHED");
      }
      if (filter === "published_gps") {
        q = q.not("latitude", "is", null).not("longitude", "is", null);
      }
      const { count: c, error } = await q;
      if (error) throw error;
      return c ?? 0;
    }

    const [
      sites_total,
      sites_published,
      sites_published_with_gps,
      venues_total,
      venues_published,
      venues_published_with_gps,
    ] = await Promise.all([
      count("cid_brewery_sites", "total"),
      count("cid_brewery_sites", "published"),
      count("cid_brewery_sites", "published_gps"),
      count("cid_venues", "total"),
      count("cid_venues", "published"),
      count("cid_venues", "published_gps"),
    ]);

    return {
      db: {
        sites_total,
        sites_published,
        sites_published_with_gps,
        venues_total,
        venues_published,
        venues_published_with_gps,
      },
      expected_pins: sites_published_with_gps + venues_published_with_gps,
    };
  },
);

/**
 * Full list of DB entries that SHOULD appear on the map (published + GPS).
 * Used by the audit panel to diff against the client's map feed and list any
 * missing entries. Small payload — id + name + city + country only.
 */
export const listCatalogMapAuditRows = createServerFn({ method: "GET" }).handler(
  async (): Promise<CatalogMapAuditRow[]> => {
    const { createClient } = await import("@supabase/supabase-js");
    const supabase = createClient(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_PUBLISHABLE_KEY!,
      { auth: { persistSession: false, autoRefreshToken: false } },
    );

    async function fetchAll(
      table: "cid_brewery_sites" | "cid_venues",
    ): Promise<CatalogMapAuditRow[]> {
      const layer: CatalogMapAuditRow["layer"] =
        table === "cid_venues" ? "venue" : "brewery_site";
      const pageSize = 1000;
      const all: CatalogMapAuditRow[] = [];
      for (let from = 0; ; from += pageSize) {
        const { data, error } = await supabase
          .from(table)
          .select(
            "id, official_name, cid_cities(official_name), cid_countries(iso2)",
          )
          .eq("publish_status", "PUBLISHED")
          .not("latitude", "is", null)
          .not("longitude", "is", null)
          .order("id", { ascending: true })
          .range(from, from + pageSize - 1);
        if (error) throw error;
        const rows = (data ?? []) as unknown as Array<{
          id: string;
          official_name: string | null;
          cid_cities: { official_name?: string } | null;
          cid_countries: { iso2?: string } | null;
        }>;
        for (const r of rows) {
          all.push({
            id: r.id,
            layer,
            name: r.official_name ?? "",
            city: r.cid_cities?.official_name ?? null,
            country_iso2: r.cid_countries?.iso2 ?? null,
          });
        }
        if (rows.length < pageSize) break;
      }
      return all;
    }

    const [sites, venues] = await Promise.all([
      fetchAll("cid_brewery_sites"),
      fetchAll("cid_venues"),
    ]);
    return [...sites, ...venues];
  },
);

export type CountryCoverageRow = {
  iso2: string;
  name_en: string;
  sites_total: number;
  sites_published: number;
  sites_published_with_gps: number;
  venues_published_with_gps: number;
  status: "PUBLISHED" | "IMPORTED_NOT_PUBLISHED" | "NOT_IMPORTED";
};

/**
 * Per-country coverage summary for the /mapa overview panel.
 * Lists all catalogued countries with published-brewery-site counts,
 * plus a derived status: PUBLISHED / IMPORTED_NOT_PUBLISHED / NOT_IMPORTED.
 */
export const listCatalogCountryCoverage = createServerFn({ method: "GET" }).handler(
  async (): Promise<CountryCoverageRow[]> => {
    const { createClient } = await import("@supabase/supabase-js");
    const supabase = createClient(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_PUBLISHABLE_KEY!,
      { auth: { persistSession: false, autoRefreshToken: false } },
    );

    const { data: countries, error: cErr } = await supabase
      .from("cid_countries")
      .select("id, iso2, official_name_en, short_name");
    if (cErr) throw cErr;

    async function fetchAllMini(table: "cid_brewery_sites" | "cid_venues") {
      const pageSize = 1000;
      const all: Array<{ country_id: string | null; publish_status: string | null; latitude: number | null; longitude: number | null }> = [];
      for (let from = 0; ; from += pageSize) {
        const { data, error } = await supabase
          .from(table)
          .select("country_id, publish_status, latitude, longitude")
          .order("id", { ascending: true })
          .range(from, from + pageSize - 1);
        if (error) throw error;
        const rows = (data ?? []) as typeof all;
        all.push(...rows);
        if (rows.length < pageSize) break;
      }
      return all;
    }

    const [sites, venues] = await Promise.all([
      fetchAllMini("cid_brewery_sites"),
      fetchAllMini("cid_venues"),
    ]);

    type Bucket = { total: number; published: number; published_gps: number };
    const sitesByCountry = new Map<string, Bucket>();
    const venuesGpsByCountry = new Map<string, number>();

    for (const s of sites) {
      if (!s.country_id) continue;
      const b = sitesByCountry.get(s.country_id) ?? { total: 0, published: 0, published_gps: 0 };
      b.total += 1;
      if (s.publish_status === "PUBLISHED") {
        b.published += 1;
        if (s.latitude != null && s.longitude != null) b.published_gps += 1;
      }
      sitesByCountry.set(s.country_id, b);
    }
    for (const v of venues) {
      if (!v.country_id) continue;
      if (v.publish_status === "PUBLISHED" && v.latitude != null && v.longitude != null) {
        venuesGpsByCountry.set(v.country_id, (venuesGpsByCountry.get(v.country_id) ?? 0) + 1);
      }
    }

    const rows: CountryCoverageRow[] = (countries ?? []).map((c) => {
      const b = sitesByCountry.get(c.id) ?? { total: 0, published: 0, published_gps: 0 };
      const venuesGps = venuesGpsByCountry.get(c.id) ?? 0;
      const publishedWithGps = b.published_gps + venuesGps;
      let status: CountryCoverageRow["status"];
      if (publishedWithGps > 0) status = "PUBLISHED";
      else if (b.total > 0) status = "IMPORTED_NOT_PUBLISHED";
      else status = "NOT_IMPORTED";
      return {
        iso2: c.iso2 ?? "",
        name_en: c.short_name ?? c.official_name_en ?? c.iso2 ?? "",
        sites_total: b.total,
        sites_published: b.published,
        sites_published_with_gps: b.published_gps,
        venues_published_with_gps: venuesGps,
        status,
      };
    });

    rows.sort((a, b) => {
      const rank = (s: CountryCoverageRow["status"]) =>
        s === "PUBLISHED" ? 0 : s === "IMPORTED_NOT_PUBLISHED" ? 1 : 2;
      const dr = rank(a.status) - rank(b.status);
      if (dr !== 0) return dr;
      const dp = (b.sites_published_with_gps + b.venues_published_with_gps) -
        (a.sites_published_with_gps + a.venues_published_with_gps);
      if (dp !== 0) return dp;
      return a.name_en.localeCompare(b.name_en, "cs");
    });
    return rows;
  },
);
